import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useConcepts, useEdgesFrom, useEdgesTo, useRecord } from '@/db/hooks'
import { mutate } from '@/runtime'
import { Button, PageTitle, SectionTitle, Sheet, cx } from '@/components/ui'
import { MdLite } from '@/lib/mdlite'
import { FAMILIARITIES, type Familiarity } from '@/kinds/learn/schema'
import type { ConceptResource } from '@/kinds/learn/apply'
import { FAMILIARITY_WORD, matchConcept, relatedOf } from '@/lib/learn'
import { Gone } from '../NotFound'

type TextField = 'my_notes' | 'when_to_use' | 'when_not_to'

/**
 * A block of markdown that is edited where it stands: tap it, type, and it is saved when the field
 * loses focus (the write goes to the device first, as every write does). Empty says what goes here.
 */
const Editable = ({
  id,
  field,
  value,
  empty,
}: {
  id: string
  field: TextField
  value: string
  empty: string
}) => {
  const [draft, setDraft] = useState<string | null>(null)
  const ref = useRef<HTMLTextAreaElement>(null)
  const editing = draft !== null
  // grows with its text, so nothing scrolls inside the card
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [draft])
  useEffect(() => {
    if (editing) ref.current?.focus()
  }, [editing])
  const save = () => {
    const next = (draft ?? '').trim()
    setDraft(null)
    if (next === value.trim()) return
    void mutate.patch(id, { spec: { [field]: next || null } })
  }
  return editing ? (
    <div>
      <textarea
        ref={ref}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        rows={4}
        className="w-full resize-none overflow-hidden text-base"
        aria-label={empty}
        data-testid={`concept-${field}-input`}
      />
      <div className="flex justify-end mt-1">
        {/* mouse down saves before the field blurs; the blur then finds nothing changed */}
        <Button variant="quiet" big onMouseDown={(e) => e.preventDefault()} onClick={save}>
          Done
        </Button>
      </div>
    </div>
  ) : (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setDraft(value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setDraft(value)
        }
      }}
      className="block w-full text-left cursor-text min-h-11 py-1 -mx-1 px-1 rounded-[var(--radius-control)] hover:bg-raised"
      data-testid={`concept-${field}`}
    >
      {value.trim() ? (
        <MdLite text={value} className="text-[15px] text-fg-2" />
      ) : (
        <span className="text-[15px] text-faint">{empty}</span>
      )}
    </div>
  )
}

/** Tick the concepts this one relates to; a tick is a related_to edge, either way round. */
const RelateSheet = ({ concept, onClose }: { concept: ConceptResource; onClose: () => void }) => {
  const [q, setQ] = useState('')
  const all = (useConcepts() ?? []).filter((c) => !c.deleted_at && c.id !== concept.id)
  const out = useEdgesFrom(concept.id)
  const into = useEdgesTo(concept.id)
  const linked = (id: string) =>
    out.some((e) => e.relation === 'related_to' && e.to_id === id) ||
    into.some((e) => e.relation === 'related_to' && e.from_id === id)
  const toggle = (id: string) => {
    if (out.some((e) => e.relation === 'related_to' && e.to_id === id))
      return mutate.setEdge(concept.id, id, 'related_to', true)
    if (into.some((e) => e.relation === 'related_to' && e.from_id === id))
      return mutate.setEdge(id, concept.id, 'related_to', true)
    return mutate.setEdge(concept.id, id, 'related_to')
  }
  return (
    <Sheet open onClose={onClose} title="Related concepts">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search concepts"
        className="w-full min-h-11 mb-3"
        type="search"
        aria-label="Search concepts"
      />
      <ul className="group-list mb-4">
        {all
          .filter((c) => matchConcept(c, q))
          .sort((a, b) => a.spec.name.localeCompare(b.spec.name))
          .map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => void toggle(c.id)}
                aria-pressed={linked(c.id)}
                className="w-full flex items-center justify-between gap-3 px-3.5 min-h-12 text-left"
              >
                <span className="truncate text-[15px]">{c.spec.name}</span>
                <span className={cx('text-sm shrink-0', linked(c.id) ? 'text-accent-text' : 'text-faint')}>
                  {linked(c.id) ? 'Related' : 'Add'}
                </span>
              </button>
            </li>
          ))}
      </ul>
    </Sheet>
  )
}

/** One concept: what it is, what I wrote about it, when to reach for it and when not to. */
export const ConceptView = () => {
  const { id } = useParams({ strict: false }) as { id: string }
  const navigate = useNavigate()
  const concept = useRecord(id) as ConceptResource | null | undefined
  const concepts = useConcepts() ?? []
  const out = useEdgesFrom(id)
  const into = useEdgesTo(id)
  const [relating, setRelating] = useState(false)
  if (concept === undefined) return null
  if (!concept || concept.deleted_at || concept.kind !== 'Concept')
    return <Gone kind="concept" deleted={!!concept?.deleted_at} />
  const related = relatedOf(id, concepts, [...out, ...into])
  const host = (() => {
    try {
      return concept.spec.source_url ? new URL(concept.spec.source_url).host : ''
    } catch {
      return ''
    }
  })()
  const set = (state: Familiarity) => void mutate.action(id, 'set_familiarity', { state })
  const remove = async () => {
    await mutate.remove(id)
    void navigate({ to: '/learn/library' })
  }
  return (
    <div data-testid="concept" className="pb-28">
      <Link
        to="/learn/library"
        className="inline-flex items-center min-h-11 text-sm text-muted hover:text-fg"
      >
        ← Library
      </Link>
      <PageTitle eyebrow={concept.spec.category}>{concept.spec.name}</PageTitle>
      {concept.spec.one_liner && <p className="text-[17px] text-fg-2 -mt-2 mb-4">{concept.spec.one_liner}</p>}
      <div className="flex flex-wrap gap-1.5 mb-3" role="group" aria-label="Familiarity">
        {FAMILIARITIES.map((f) => (
          <Button
            key={f}
            variant={concept.status.familiarity === f ? 'chipOn' : 'chip'}
            className="min-h-11 px-4"
            disabled={concept.status.familiarity === f}
            onClick={() => set(f)}
            data-testid={`concept-familiarity-${f}`}
          >
            {FAMILIARITY_WORD[f]}
          </Button>
        ))}
      </div>
      {concept.spec.source_url && host && (
        <a
          href={concept.spec.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center min-h-11 text-[15px] text-accent-text underline underline-offset-2"
          data-testid="concept-source"
        >
          Read more on {host}
        </a>
      )}

      <div className="mt-4">
        <SectionTitle>My notes</SectionTitle>
        <Editable id={id} field="my_notes" value={concept.spec.my_notes ?? ''} empty="Add your notes" />
      </div>
      <div className="mt-4">
        <SectionTitle>When to use</SectionTitle>
        <Editable
          id={id}
          field="when_to_use"
          value={concept.spec.when_to_use ?? ''}
          empty="When does it fit?"
        />
      </div>
      <div className="mt-4">
        <SectionTitle>When not to</SectionTitle>
        <Editable
          id={id}
          field="when_not_to"
          value={concept.spec.when_not_to ?? ''}
          empty="When is it the wrong tool?"
        />
      </div>

      <div className="mt-6">
        <SectionTitle
          right={
            <Button variant="quiet" onClick={() => setRelating(true)} data-testid="concept-relate">
              Relate
            </Button>
          }
        >
          Related
        </SectionTitle>
        {related.length > 0 && (
          <ul className="group-list" data-testid="concept-related">
            {related.map((c) => (
              <li key={c.id}>
                <Link
                  to="/learn/concept/$id"
                  params={{ id: c.id }}
                  className="flex items-center min-h-12 px-3.5 text-[15px]"
                >
                  {c.spec.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-8">
        <Button variant="quiet" big onClick={() => void remove()} data-testid="concept-delete">
          Delete this concept
        </Button>
      </div>
      {relating && <RelateSheet concept={concept} onClose={() => setRelating(false)} />}
    </div>
  )
}
